/**
 * @file SubmissionForm.tsx
 * @description
 * Displays the main IRB study submission form for the user (PI, sponsor, or CRO).
 * This file implements:
 *  1. Creating a new DRAFT submission if none exists (via initializeSubmission).
 *  2. Editing an existing DRAFT if an `edit` query param is found.
 *  3. Handling multi-step finalization flow: PDF/MD generation, doc marking, status update, etc.
 *
 * @dependencies
 * - React, React Router
 * - react-hook-form for form state
 * - docStorageService, docGenerationService, docVersionService, submissionService
 * - useFormProgress, useAutosave for UI features
 *
 * @notes
 * - The main new feature in Step 3 is reading an `edit=someSubmissionId` from the query param
 *   to load an existing submission's data.
 * - If no `edit` param, we create a brand-new draft submission as before.
 * - We provide full file content here to show the entire code, with the new logic in the "loadExistingSubmission" path.
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeSubmission } from '../services/submissionService';
import { useAuth } from '../hooks/useAuth';
import { useForm } from 'react-hook-form';
import UserNavBar from '../components/UserNavBar';
import { useAutosave } from '../hooks/useAutosave';
import { useFormProgress } from '../hooks/useFormProgress';
import {
  FormSubmissionFields,
  FormData,
  formSections,
} from '../components/FormSubmissionFields';
import { submitDocumentPackage } from '../services/submissionDocumentPackage';
import { SubmissionProgressModal } from '../components/SubmissionProgressModal';
import { useSupabase } from '../contexts/SupabaseContext';
import withIdleTimer from '../hoc/withIdleTimer';

// Add this type definition for submission status
type SubmissionStatus = 'DRAFT' | 'IRB_REVIEW' | 'APPROVED' | 'REJECTED' | 'REVISIONS_NEEDED' | string;

/**
 * A short helper to log form or finalization operations with a consistent structure.
 */
function logFormOperation(operation: string, details: Record<string, any>, error?: any) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && {
      error:
        typeof error === 'object'
          ? { message: error.message, name: error.name, stack: error.stack }
          : error,
    }),
  };

  if (error) {
    console.error(`FORM_LOG [${operation}]:`, logObj);
  } else {
    console.log(`FORM_LOG [${operation}]:`, logObj);
  }

  return logObj;
}

// Add this hook near the top of the file, before the FormComponent
function useFormLock(submissionId: string | null, lockTimeoutMinutes = 30) {
  const [isLocked, setIsLocked] = useState(false);
  const [lockMessage, setLockMessage] = useState('');

  useEffect(() => {
    if (!submissionId) return;

    // Create a unique tab ID if one doesn't exist
    if (!window.name || window.name.length === 0) {
      window.name = `tab_${Date.now()}_${Math.random().toString(36).substr(2,9)}`;
    }

    const tabId = window.name;
    const lockKey = `form_lock_${submissionId}`;
    const lockTimeoutMs = lockTimeoutMinutes * 60 * 1000;

    // Helper functions for localStorage with error handling
    const safeGetItem = (key: string) => {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        console.error('Error accessing localStorage:', e);
        return null;
      }
    };

    const safeSetItem = (key: string, value: string): boolean => {
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.error('Error writing to localStorage:', e);
        return false;
      }
    };

    const safeRemoveItem = (key: string): boolean => {
      try {
        localStorage.removeItem(key);
        return true;
      } catch (e) {
        console.error('Error removing from localStorage:', e);
        return false;
      }
    };

    // Check for existing lock
    const checkAndSetLock = () => {
      const existingLockData = safeGetItem(lockKey);

      if (existingLockData) {
        try {
          const lock = JSON.parse(existingLockData);

          // Check if lock has expired
          const now = Date.now();
          const lockAge = now - lock.timestamp;

          if (lockAge > lockTimeoutMs) {
            // Lock has expired, we can take over
            setNewLock();
            return;
          }

          // Check if lock is from another tab
          if (lock.tabId !== tabId) {
            // Calculate minutes ago for the message
            const minutesAgo = Math.floor(lockAge / 60000);
            setLockMessage(`This form was opened ${minutesAgo} minute${minutesAgo !== 1 ? 's' : ''} ago in another tab or window.`);
            setIsLocked(true);
            return;
          }

          // We already own the lock, just update the timestamp
          lock.timestamp = now;
          safeSetItem(lockKey, JSON.stringify(lock));
        } catch (e) {
          // Invalid JSON, create a new lock
          console.error('Invalid lock data, creating new lock');
          setNewLock();
        }
      } else {
        // No existing lock, create a new one
        setNewLock();
      }
    };

    const setNewLock = () => {
      const lockData = {
        tabId: tabId,
        timestamp: Date.now(),
        // Store additional info that might be helpful for debugging
        userAgent: navigator.userAgent,
        pageUrl: window.location.href
      };

      safeSetItem(lockKey, JSON.stringify(lockData));
      setIsLocked(false);
    };

    // Initialize lock
    checkAndSetLock();

    // Set up interval to refresh our lock
    const interval = setInterval(() => {
      // If we're already locked, just monitor if lock has been released
      if (isLocked) {
        const existingLockData = safeGetItem(lockKey);
        if (!existingLockData) {
          // Lock has been released
          setNewLock();
          setIsLocked(false);
        }
      } else {
        // Refresh our lock
        checkAndSetLock();
      }
    }, 10000); // Check every 10 seconds

    // Set up storage event listener to react to changes from other tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === lockKey) {
        // If our lock was removed by another tab
        if (!e.newValue && !isLocked) {
          // Try to reclaim the lock
          setTimeout(checkAndSetLock, Math.random() * 1000); // Random delay to avoid race conditions
        } else if (e.newValue && !isLocked) {
          // Another tab might have claimed the lock, check if it's ours
          checkAndSetLock();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Release lock when component unmounts
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);

      // Only remove the lock if we own it
      const existingLockData = safeGetItem(lockKey);
      if (existingLockData) {
        try {
          const lock = JSON.parse(existingLockData);
          if (lock.tabId === tabId) {
            safeRemoveItem(lockKey);
          }
        } catch (e) {
          console.error('Error parsing lock data during cleanup:', e);
        }
      }
    };
  }, [submissionId, lockTimeoutMinutes, isLocked]);

  return { isLocked, lockMessage };
}

const FormComponent: React.FC = () => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({ mode: 'onChange' });

  const navigate = useNavigate();
  const location = useLocation(); // used to check query params
  const supabase = useSupabase();
  const { user, signOut, loading: authLoading, sessionExpired } = useAuth();

  // The submission ID is set after calling initializeSubmission(...) or loadExistingSubmission
  const [documentsPending, setDocumentsPending] = useState(true);
  const [submissionId, setSubmissionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  // Add this state for submission status
  const [, setSubmissionStatus] = useState<SubmissionStatus>('DRAFT');

  // Add refs to track state and prevent duplicate operations
  const isFetchingRef = useRef(false);
  const userIdRef = useRef<string | null>(null);

  const watchAll = watch();
  const userId = user?.id || 'anonymous-user';

  // We'll track the overall form progress (for the top bar).
  const { overallCompletion, sectionProgress } = useFormProgress(formSections as any, watchAll);

  // For partial "jump to section" using refs
  const sectionRefs = useMemo(
    () =>
      Array(formSections.length)
        .fill(null)
        .map(() => React.createRef<HTMLDivElement>()),
    []
  );

  // Function to scroll to a specific section
  const scrollToSection = (index: number) => {
    if (sectionRefs[index] && sectionRefs[index].current) {
      sectionRefs[index].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const redirectToReadOnlySubmission = useCallback((existingId: string) => {
    navigate(`/submissions/${existingId}`, {
      replace: true,
      state: {
        accessMessage: 'You can view this submission, but only the submission owner can edit it.',
      },
    });
  }, [navigate]);

  // Autosave config: only valid if we have a submission ID
  const autosaveConfig =
    !loading && submissionId
      ? {
          formData: watchAll,
          submissionId,
          userId,
          createdAt: '2025-02-20T14:30:00Z', // example
        }
      : null;

  const { savingStatus, handleImmediateAutosave } = useAutosave(autosaveConfig);

  // For final multi-step submission:
  const [progressModalOpen, setProgressModalOpen] = useState(false);
  const [progressStep, setProgressStep] = useState(0);
  const [hasFinalError, setHasFinalError] = useState(false);
  const [finalErrorMessage, setFinalErrorMessage] = useState('');
  const [hasFinalSuccess, setHasFinalSuccess] = useState(false);
  const [finalSuccessMessage, setFinalSuccessMessage] = useState('');

  // Step messages for the SubmissionProgressModal
  const stepMessages = [
    'Checking required form fields...',
    'Storing locked form as PDF...',
    'Storing locked form as Markdown...',
    'Saving the verified document selection...',
    'Redirecting to dashboard...',
  ];

  // Add this near your other state declarations
  const { isLocked, lockMessage } = useFormLock(submissionId);

  /**
   * createNewDraftForUser
   * ---------------------
   * Fallback or default approach if we have no edit param or failed to load existing draft:
   * creates a brand-new draft submission, sets submissionId, and fetches any doc placeholders (none by default).
   */
  const createNewDraftForUser = useCallback(async () => {
    // Skip if already fetching, auth is loading, or no user
    if (isFetchingRef.current || authLoading || !user?.id) {
      return;
    }

    isFetchingRef.current = true;
    try {
      if (!user?.id || !user.email) {
        throw new Error('User ID and email are required to initialize submission.');
      }
      const res = await initializeSubmission(user.id, user.email);
      if (res?.id) {
        setSubmissionId(res.id);
        // Set default status for new submissions
        setSubmissionStatus('DRAFT');
      } else {
        throw new Error('Failed to initialize submission: No ID returned.');
      }

      // The document workspace loads current rows independently of saved form data.

    } catch (err) {
      console.error('Error creating new draft submission:', err);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [authLoading, user]);

  /**
   * Attempt to load an existing submission if ?edit=someID is present in the query string
   * If found, set submissionId and also load doc references from DB.
   */
  const loadExistingSubmission = useCallback(async (existingId: string) => {
    // Skip if already fetching or no user
    if (isFetchingRef.current || !user?.id) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);

    try {
      // fetch from Supabase
      const { data, error } = await supabase
        .from('submissions')
        .select('*')
        .eq('id', existingId)
        .eq('user_id', user?.id || '')
        .single();

      if (error || !data || !user?.id) {
        console.warn('No editable draft found, redirecting to read-only submission.', error);
        redirectToReadOnlySubmission(existingId);
        return;
      }

      // If found, set the submission
      setSubmissionId(data.id);
      // Set the submission status
      setSubmissionStatus(data.status || 'DRAFT');

      // load form data from data.data
      if (data.data && typeof data.data === 'object') {
        Object.entries(data.data).forEach(([key, value]) => {
          setValue(key as any, value, { shouldValidate: false });
        });
      }

      // The document workspace loads current rows independently of saved form data.

    } catch (err) {
      console.error('Error loading existing submission:', err);
      redirectToReadOnlySubmission(existingId);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [redirectToReadOnlySubmission, supabase, user, setValue]);

  /**
   * On mount, decide whether we have ?edit=someID or not
   * If yes, try to load that submission. If not, create a new one.
   */
  useEffect(() => {
    // If userId ref changes, update it
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id || null;
    }

    // Skip if auth is still loading
    if (authLoading) {
      return;
    }

    if (!user?.id) {
      // Not logged in, skip
      setLoading(false);
      return;
    }

    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');

    if (editId) {
      logFormOperation('loadExisting', { editId });
      loadExistingSubmission(editId);
    } else {
      logFormOperation('createNewDraft', { userId: user.id });
      createNewDraftForUser();
    }
  }, [user?.id, location.search, authLoading, loadExistingSubmission, createNewDraftForUser]);

  // Add effect to handle sessionExpired
  useEffect(() => {
    if (sessionExpired) {
      navigate('/signin');
    }
  }, [sessionExpired, navigate]);


  /**
   * finalizeSubmission
   * ------------------
   * Orchestrates the multi-step finalization flow:
   * 1. Validate (react-hook-form)
   * 2. Generate PDF, upload
   * 3. Generate Markdown, upload
   * 4. Collect user-uploaded doc IDs still present in the final submission data
   * 5. Mark older documents inactive except for these user docs + the new PDF/MD
   * 6. Update submission status => IRB_REVIEW
   * 7. Show success or error
   * 8. Redirect
   */
  const finalizeSubmission = async (data: FormData) => {
    if (!submissionId) {
      alert('No submission ID found. Cannot finalize submission.');
      return;
    }

    if (documentsPending) { alert('Finish or discard pending uploads and resolve document errors before submitting.'); return; }

    // Reset any existing final states
    setHasFinalError(false);
    setFinalErrorMessage('');
    setHasFinalSuccess(false);
    setFinalSuccessMessage('');

    // Open progress modal
    setProgressModalOpen(true);
    setProgressStep(0);

    try {
      await submitDocumentPackage(submissionId, data, formSections, setProgressStep);
      setSubmissionStatus('IRB_REVIEW');

      // Step 5: "Redirecting to dashboard..."
      setProgressStep(4);

      setTimeout(() => {
        setHasFinalSuccess(true);
        setFinalSuccessMessage('Submission finalized successfully!');
        setTimeout(() => {
          setProgressModalOpen(false);
          navigate('/dashboard');
        }, 1200);
      }, 600);
    } catch (error) {
      console.error('Error in finalizeSubmission:', error);
      setHasFinalError(true);
      setFinalErrorMessage(error instanceof Error ? error.message : String(error));
    }
  };

  /**
   * Standard user logout
   */
  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // The onSubmit from react-hook-form is used for final validation
  const onSubmit = (data: FormData) => {
    finalizeSubmission(data);
  };

  // Add loading state UI for auth loading
  if (authLoading) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="h-16"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F55D3E] mx-auto mb-4"></div>
            <p className="text-gray-600">Verifying your account...</p>
          </div>
        </div>
      </div>
    );
  }

  // Add this check before your main return statement
  if (isLocked) {
    return (
      <div className="flex flex-col h-screen">
        <UserNavBar user={user} onSignOut={handleLogout} />
        <div className="h-16"></div>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center p-6 max-w-md bg-amber-50 border border-amber-200 rounded-lg">
            <div className="text-amber-600 text-3xl mb-2">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">This Form Is Already Open</h2>
            <p className="mb-4 text-gray-600">
              {lockMessage || 'This submission is currently being edited in another browser tab.'}
            </p>
            <p className="mb-4 text-gray-600">
              Please continue editing in that tab to avoid conflicts.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 bg-[#F55D3E] text-white rounded-md"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen">
      <UserNavBar user={user} onSignOut={handleLogout} />

       {/* Add spacing below navbar */}
    <div className="h-16"></div>

      {/* Autosave notification in top-right corner */}
      <div className="fixed top-28 right-6 z-50">
        {savingStatus === 'saving' && (
          <div className="p-2 rounded-md bg-[#F9A594] text-[#343a40] shadow-md">Saving...</div>
        )}
        {savingStatus === 'saved' && (
          <div className="p-2 rounded-md bg-[#D4F5E9] text-[#343a40] shadow-md">Saved</div>
        )}
        {savingStatus === 'error' && (
          <div className="p-2 rounded-md bg-[#B83EF5] text-white shadow-md">Autosave Error</div>
        )}
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Fixed Sidebar for Section Navigation */}
        <div className="hidden lg:block w-64 shrink-0 bg-white shadow-md border-r border-gray-200 overflow-y-auto">
          <div className="p-4">
            {/* Overall progress display at the top */}
            <div className="mb-4 pb-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-500 mb-2">OVERALL PROGRESS</h3>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xl font-bold text-[#343a40]">{overallCompletion}%</span>
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                  overallCompletion === 100 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                }`}>
                  {overallCompletion === 100 ? 'Complete' : 'In Progress'}
                </span>
              </div>
              <div className="w-full h-2 bg-[#f8f9fa] rounded-full border border-gray-200">
                <div
                  className="h-2 rounded-full transition-all duration-300 ease-in-out"
                  style={{
                    width: `${overallCompletion}%`,
                    backgroundColor: overallCompletion === 100 ? '#7AF53E' : '#F55D3E',
                  }}
                />
              </div>
            </div>

            <h2 className="text-lg font-bold text-[#343a40] mb-3">Form Sections</h2>
            <ul className="space-y-2">
              {sectionProgress.map((section, idx) => (
                <li key={`section-${idx}`} className="mb-3">
                  <button
                    onClick={() => scrollToSection(idx)}
                    className="w-full text-left py-2 px-3 rounded text-sm font-medium transition-colors hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#F55D3E] focus:ring-opacity-50"
                  >
                    {section.heading}
                  </button>
                  <div className="mt-1 px-3 flex items-center justify-between">
                    <span className={`text-xs font-medium ${
                      section.completion === 100
                        ? 'text-green-600'
                        : section.completion > 50
                        ? 'text-yellow-600'
                        : 'text-red-600'
                    }`}>
                      {section.completion}% complete
                    </span>
                    <div className="w-16 h-1.5 bg-gray-200 rounded-full">
                      <div
                        className={`h-1.5 rounded-full ${
                          section.completion === 100
                            ? 'bg-green-500'
                            : section.completion > 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                        style={{ width: `${section.completion}%` }}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Support text */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              <p>Questions? Check out our <a href="https://timelyirb.com/faqs" target="_blank" rel="noopener noreferrer" className="text-[#F55D3E] hover:underline">FAQs</a>, find more info on <a href="https://timelyirb.com/review-process" target="_blank" rel="noopener noreferrer" className="text-[#F55D3E] hover:underline">our review process</a>, or email <a href="mailto:support@timelyirb.com" className="text-[#F55D3E] hover:underline">support@timelyirb.com</a></p>
            </div>
          </div>
        </div>

        {/* Main form content */}
        <div className="min-w-0 flex-1 overflow-auto relative">
          {loading ? (
            <div className="p-6 text-center">Loading...</div>
          ) : (
            <FormSubmissionFields
              onSubmit={onSubmit}
              register={register}
              errors={errors}
              watchAll={watchAll}
              setValue={setValue}
              handleImmediateAutosave={handleImmediateAutosave}
              handleSubmit={handleSubmit}
              savingStatus={savingStatus}
              loading={false}
              sectionRefs={sectionRefs}

              submissionId={submissionId}
              userId={user?.id}
              onDocumentsPendingChange={setDocumentsPending}
            />
          )}
        </div>
      </div>

      {/* Submission Progress Modal */}
      <SubmissionProgressModal
        isOpen={progressModalOpen}
        stepMessages={stepMessages}
        currentStep={progressStep}
        hasError={hasFinalError}
        errorMessage={finalErrorMessage}
        hasSuccess={hasFinalSuccess}
        successMessage={finalSuccessMessage}
        onClose={() => {
          // If user closes the modal while in error or success, let them see the form
          setProgressModalOpen(false);
        }}
        onRetry={() => {
          // If we want to allow a retry for the final step
          setProgressModalOpen(false);
          setProgressStep(0);
          setHasFinalError(false);
          setFinalErrorMessage('');
        }}
        showCloseButton={false}
      />
    </div>
  );
};

export const SubmissionForm = withIdleTimer(FormComponent);
